import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Logo } from "@/components/site/Logo";
import { api } from "@/lib/api";

const initial = {
  nome_cognome: "",
  codice_fiscale: "",
  telefono: "",
  email: "",
  professione: "",
  ragione_sociale: "",
  partita_iva: "",
  codice_fiscale_azienda: "",
  indirizzo_sede: "",
  cap_citta_prov: "",
  codice_sdi: "",
  pec: "",
  email_contabilita: "",
  consenso_dati: false,
  consenso_marketing: null,
  note: "",
  // Quota cena e pernotto in convenzione
  cena_13_nov: false,
  pernotto_13_nov: false,
  cena_27_nov: false,
  pernotto_27_nov: false,
  cena_pernotto_persone: 1,
};

const fieldClass =
  "w-full rounded-lg border border-[#D8DEEC] bg-white px-4 py-3 font-body text-base text-[#16256B] placeholder:text-[#9AA3B8] transition-colors duration-200 focus:border-[#2D46A0] focus:outline-none focus:ring-1 focus:ring-[#2D46A0]";

const Field = ({ label, hint, id, children, required }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="font-body text-sm font-medium text-[#16256B]">
      {label} {required && <span className="text-[#C9A227]">*</span>}
      {hint && <span className="ml-2 font-normal text-[#8A93A8]">({hint})</span>}
    </label>
    {children}
  </div>
);

const OptionRow = ({ id, title, meta, price, checked, onChange, dataTestId }) => (
  <label
    htmlFor={id}
    className={`flex cursor-pointer items-start gap-4 rounded-xl border p-4 transition-colors ${
      checked ? "border-[#C9A227] bg-[#C9A227]/5" : "border-[#E4E8F2] hover:bg-[#F7F9FD]"
    }`}
    data-testid={`${dataTestId}-label`}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="mt-1 h-4 w-4 accent-[#C9A227]"
      data-testid={dataTestId}
    />
    <div className="flex-1">
      <p className="font-display text-sm font-bold text-[#16256B]">{title}</p>
      <p className="mt-1 font-body text-xs text-[#4A5268]">{meta}</p>
    </div>
    <span className="shrink-0 font-display text-sm font-black text-[#16256B]">{price}</span>
  </label>
);

export default function Iscrizione() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Iscrizione — Visioni di Impresa 2026";
  }, []);

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  const extras = useMemo(() => {
    const picks = [];
    const p = form.cena_pernotto_persone || 1;
    if (form.cena_13_nov) picks.push({ label: "Cena ven 13/11", amount: 60 * p });
    if (form.pernotto_13_nov) picks.push({ label: "Pernotto ven 13/11", amount: 150 });
    if (form.cena_27_nov) picks.push({ label: "Cena ven 27/11", amount: 60 * p });
    if (form.pernotto_27_nov) picks.push({ label: "Pernotto ven 27/11", amount: 150 });
    const tot = picks.reduce((s, x) => s + x.amount, 0);
    return { picks, tot };
  }, [form]);

  const anyExtras = extras.picks.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consenso_dati) {
      toast.error("Il consenso al trattamento dati è obbligatorio.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/iscrizioni", form);
      setDone(true);
      toast.success(data.message || "Prenotazione ricevuta.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'invio. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB]">
      <header className="border-b border-[#E4E8F2] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="back-home-link">
            <Logo variant="dark" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-body text-sm text-[#4A5268] transition-colors hover:text-[#16256B]"
            data-testid="back-home-arrow"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alla landing
          </Link>
        </div>
      </header>

      <section className="bg-[#0E1A4A] py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">
            Modulo di iscrizione
          </p>
          <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[0.98] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Visioni di <span className="text-[#C9A227]">Impresa</span>
          </h1>
          <p className="mt-3 font-display text-base font-bold uppercase tracking-tight text-white/90 sm:text-xl">
            Formare gli imprenditori di oggi. Costruire quelli di domani. Adesso.
          </p>
          <p className="mt-5 max-w-2xl font-body text-base text-white/70 sm:text-lg">
            13&ndash;14 e 27&ndash;28 Novembre 2026 &middot; Secolario &mdash; Masseria del Viverbene, Palmariggi (Le)
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-20">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-[#E4E8F2] bg-white p-10 text-center shadow-sm sm:p-14"
            data-testid="iscrizione-success"
          >
            <CheckCircle2 className="mx-auto h-16 w-16 text-[#C9A227]" />
            <h2 className="mt-6 font-display text-3xl font-black uppercase text-[#16256B]">
              Prenotazione ricevuta
            </h2>
            <p className="mx-auto mt-4 max-w-lg font-body text-base text-[#4A5268]">
              Abbiamo preso in carico la Sua richiesta. La nostra Segreteria la contatterà a breve con tutte le indicazioni operative e le modalità per perfezionare l&rsquo;iscrizione.
            </p>
            <div className="mt-8 inline-flex flex-col gap-1 rounded-xl bg-[#F4F6FB] px-6 py-4 text-left">
              <span className="font-body text-xs uppercase tracking-overline text-[#8A93A8]">Segreteria</span>
              <a href="tel:+390832393823" className="font-display text-base font-bold text-[#16256B]">0832 / 393823</a>
              <a href="mailto:segreteria@fondazionesemidimpresa.it" className="font-body text-sm text-[#2D46A0]">segreteria@fondazionesemidimpresa.it</a>
            </div>
            <div className="mt-10">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full bg-[#16256B] px-6 py-3 font-display text-sm font-bold text-white"
                data-testid="return-home-btn"
              >
                Torna alla landing
              </Link>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-10" data-testid="iscrizione-form">
            {/* 01 Partecipante */}
            <section className="rounded-3xl border border-[#E4E8F2] bg-white p-6 shadow-sm sm:p-10">
              <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">01 · Partecipante</p>
              <h2 className="mt-3 font-display text-2xl font-black uppercase text-[#16256B] sm:text-3xl">
                Dati del Partecipante
              </h2>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <Field label="Nome e Cognome" id="nome_cognome" required>
                  <input id="nome_cognome" required value={form.nome_cognome} onChange={update("nome_cognome")} className={fieldClass} placeholder="Nome Cognome" data-testid="input-nome-cognome" />
                </Field>
                <Field label="Codice Fiscale" id="codice_fiscale" required>
                  <input id="codice_fiscale" required value={form.codice_fiscale} onChange={update("codice_fiscale")} className={`${fieldClass} uppercase`} placeholder="RSSMRA85T10A562S" maxLength={16} data-testid="input-codice-fiscale" />
                </Field>
                <Field label="Telefono / Cellulare" id="telefono" required>
                  <input id="telefono" required value={form.telefono} onChange={update("telefono")} className={fieldClass} placeholder="+39 ..." data-testid="input-telefono" />
                </Field>
                <Field label="E-mail personale" id="email" required>
                  <input id="email" type="email" required value={form.email} onChange={update("email")} className={fieldClass} placeholder="nome@email.it" data-testid="input-email" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Professione / Ruolo" id="professione" required>
                    <input id="professione" required value={form.professione} onChange={update("professione")} className={fieldClass} placeholder="Es. Imprenditore, Founder, Manager..." data-testid="input-professione" />
                  </Field>
                </div>
              </div>
            </section>

            {/* 02 Fatturazione */}
            <section className="rounded-3xl border border-[#E4E8F2] bg-white p-6 shadow-sm sm:p-10">
              <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">02 · Fatturazione</p>
              <h2 className="mt-3 font-display text-2xl font-black uppercase text-[#16256B] sm:text-3xl">
                Dati per la Fatturazione
              </h2>
              <p className="mt-3 font-body text-sm text-[#4A5268]">
                Compilare se la fattura va intestata all&rsquo;azienda o al libero professionista.
              </p>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Ragione Sociale / Intestatario Fattura" id="ragione_sociale">
                    <input id="ragione_sociale" value={form.ragione_sociale} onChange={update("ragione_sociale")} className={fieldClass} data-testid="input-ragione-sociale" />
                  </Field>
                </div>
                <Field label="Partita IVA" id="partita_iva">
                  <input id="partita_iva" value={form.partita_iva} onChange={update("partita_iva")} className={fieldClass} data-testid="input-partita-iva" />
                </Field>
                <Field label="Codice Fiscale" hint="se diverso da P.IVA" id="codice_fiscale_azienda">
                  <input id="codice_fiscale_azienda" value={form.codice_fiscale_azienda} onChange={update("codice_fiscale_azienda")} className={`${fieldClass} uppercase`} data-testid="input-cf-azienda" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Indirizzo Sede Legale" id="indirizzo_sede">
                    <input id="indirizzo_sede" value={form.indirizzo_sede} onChange={update("indirizzo_sede")} className={fieldClass} data-testid="input-indirizzo-sede" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="CAP / Città / Prov." id="cap_citta_prov">
                    <input id="cap_citta_prov" value={form.cap_citta_prov} onChange={update("cap_citta_prov")} className={fieldClass} placeholder="73020 Palmariggi (LE)" data-testid="input-cap-citta-prov" />
                  </Field>
                </div>
                <Field label="Codice Destinatario (SDI)" hint="6 o 7 caratteri" id="codice_sdi">
                  <input id="codice_sdi" value={form.codice_sdi} onChange={update("codice_sdi")} className={`${fieldClass} uppercase`} maxLength={7} data-testid="input-codice-sdi" />
                </Field>
                <Field label="PEC" hint="Posta Elettronica Certificata" id="pec">
                  <input id="pec" type="email" value={form.pec} onChange={update("pec")} className={fieldClass} data-testid="input-pec" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="E-mail di contatto per la contabilità" id="email_contabilita">
                    <input id="email_contabilita" type="email" value={form.email_contabilita} onChange={update("email_contabilita")} className={fieldClass} data-testid="input-email-contabilita" />
                  </Field>
                </div>
              </div>
            </section>

            {/* 03 Contributo di partecipazione al corso */}
            <section className="rounded-3xl border border-[#E4E8F2] bg-white p-6 shadow-sm sm:p-10">
              <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">03 · Corso</p>
              <h2 className="mt-3 font-display text-2xl font-black uppercase text-[#16256B] sm:text-3xl">
                Contributo di partecipazione al corso
              </h2>

              <div className="mt-8 grid gap-5 sm:grid-cols-3">
                <div className="rounded-2xl bg-[#0E1A4A] p-6 text-white">
                  <p className="font-body text-[10px] uppercase tracking-overline text-[#C9A227]">Contributo totale</p>
                  <p className="mt-2 font-display text-3xl font-black">&euro; 600,00</p>
                  <p className="font-body text-sm text-white/60">+ IVA 22%</p>
                </div>
                <div className="rounded-2xl bg-[#F4F6FB] p-6">
                  <p className="font-body text-[10px] uppercase tracking-overline text-[#2D46A0]">Acconto</p>
                  <p className="mt-2 font-display text-3xl font-black text-[#16256B]">&euro; 180,00</p>
                  <p className="font-body text-sm text-[#4A5268]">+ IVA 22% &middot; al momento dell&rsquo;iscrizione</p>
                </div>
                <div className="rounded-2xl bg-[#F4F6FB] p-6">
                  <p className="font-body text-[10px] uppercase tracking-overline text-[#2D46A0]">Saldo</p>
                  <p className="mt-2 font-display text-3xl font-black text-[#16256B]">&euro; 420,00</p>
                  <p className="font-body text-sm text-[#4A5268]">+ IVA 22% &middot; entro il 09/11/2026</p>
                </div>
              </div>

              <p className="mt-6 font-body text-sm leading-relaxed text-[#4A5268]">
                Il contributo comprende l&rsquo;accesso ad entrambe le sessioni formative, workshop e materiali didattici, affiancamento e mentoring personalizzato durante i laboratori, welcome coffee e light lunch.
              </p>

              <div className="mt-8 rounded-2xl border border-[#E4E8F2] bg-[#F4F6FB] p-6">
                <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">Estremi per il bonifico (Fondazione)</p>
                <dl className="mt-4 grid gap-3 font-body text-sm sm:grid-cols-[180px_1fr]">
                  <dt className="text-[#8A93A8]">Intestatario Conto</dt>
                  <dd className="text-[#16256B]">Fondazione Semi di Impresa Luca Delle Donne &mdash; Impresa Sociale ETS</dd>
                  <dt className="text-[#8A93A8]">Banca</dt>
                  <dd className="text-[#16256B]">Banca di Credito Cooperativo Terra d&rsquo;Otranto</dd>
                  <dt className="text-[#8A93A8]">IBAN</dt>
                  <dd className="font-display font-bold tracking-wider text-[#16256B]" data-testid="iban-fondazione">IT21X0844516000000000976965</dd>
                  <dt className="text-[#8A93A8]">Causale</dt>
                  <dd className="text-[#16256B]">Acconto / Saldo Semi di impresa in Masseria 2026 &mdash; Nome e Cognome Partecipante</dd>
                </dl>
                <p className="mt-5 border-t border-[#E4E8F2] pt-4 font-body text-xs italic text-[#4A5268]">
                  La prenotazione si intende perfezionata e confermata solo al ricevimento della contabile dell&rsquo;avvenuto pagamento dell&rsquo;acconto (inviare la stessa a <a href="mailto:segreteria@fondazionesemidimpresa.it" className="font-semibold text-[#2D46A0] no-underline">segreteria@fondazionesemidimpresa.it</a>).
                </p>
              </div>
            </section>

            {/* 04 Quota cena e pernotto */}
            <section className="rounded-3xl border border-[#E4E8F2] bg-white p-6 shadow-sm sm:p-10">
              <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">04 · Cena &amp; Pernotto</p>
              <h2 className="mt-3 font-display text-2xl font-black uppercase text-[#16256B] sm:text-3xl">
                Quota cena e pernotto
              </h2>
              <p className="mt-3 font-body text-sm text-[#4A5268]">
                In convenzione con la struttura. Opzionali: seleziona i servizi di interesse.
              </p>

              {/* Week-end 1 */}
              <div className="mt-8">
                <p className="font-body text-[10px] uppercase tracking-overline text-[#2D46A0]">Week-end 1 &middot; 13 Novembre</p>
                <div className="mt-3 grid gap-3">
                  <OptionRow
                    id="cena_13_nov"
                    title="Cena venerdì 13 novembre"
                    meta="Volìvo Restaurant by Secolario — Masseria del Viverbene"
                    price="€ 60,00 iva incl. / persona"
                    checked={form.cena_13_nov}
                    onChange={toggle("cena_13_nov")}
                    dataTestId="opt-cena-13"
                  />
                  <OptionRow
                    id="pernotto_13_nov"
                    title="Pernotto venerdì 13 novembre"
                    meta="Secolario — Masseria del Viverbene (DUS/DBL, colazione inclusa)"
                    price="€ 150,00 iva incl."
                    checked={form.pernotto_13_nov}
                    onChange={toggle("pernotto_13_nov")}
                    dataTestId="opt-pernotto-13"
                  />
                </div>
              </div>

              {/* Week-end 2 */}
              <div className="mt-8">
                <p className="font-body text-[10px] uppercase tracking-overline text-[#2D46A0]">Week-end 2 &middot; 27 Novembre</p>
                <div className="mt-3 grid gap-3">
                  <OptionRow
                    id="cena_27_nov"
                    title="Cena venerdì 27 novembre"
                    meta="Volìvo Restaurant by Secolario — Masseria del Viverbene"
                    price="€ 60,00 iva incl. / persona"
                    checked={form.cena_27_nov}
                    onChange={toggle("cena_27_nov")}
                    dataTestId="opt-cena-27"
                  />
                  <OptionRow
                    id="pernotto_27_nov"
                    title="Pernotto venerdì 27 novembre"
                    meta="Secolario — Masseria del Viverbene (DUS/DBL, colazione inclusa)"
                    price="€ 150,00 iva incl."
                    checked={form.pernotto_27_nov}
                    onChange={toggle("pernotto_27_nov")}
                    dataTestId="opt-pernotto-27"
                  />
                </div>
              </div>

              {anyExtras && (
                <div className="mt-8 grid gap-5 sm:grid-cols-[1fr_240px] sm:items-end">
                  <Field label="Numero persone (per le cene)" id="cena_pernotto_persone">
                    <input
                      id="cena_pernotto_persone"
                      type="number"
                      min={1}
                      max={10}
                      value={form.cena_pernotto_persone}
                      onChange={(e) => setForm((f) => ({ ...f, cena_pernotto_persone: Math.max(1, Number(e.target.value) || 1) }))}
                      className={fieldClass}
                      data-testid="input-cena-persone"
                    />
                  </Field>
                  <div className="rounded-2xl border border-[#C9A227]/40 bg-[#C9A227]/5 p-5 text-right">
                    <p className="font-body text-[10px] uppercase tracking-overline text-[#9c7d18]">Totale extra</p>
                    <p className="mt-1 font-display text-3xl font-black text-[#16256B]">&euro; {extras.tot.toFixed(2)}</p>
                    <p className="font-body text-[11px] text-[#4A5268]">iva inclusa</p>
                  </div>
                </div>
              )}

              <div className="mt-8 rounded-2xl border border-[#E4E8F2] bg-[#F4F6FB] p-6">
                <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">Estremi per il bonifico (Struttura)</p>
                <dl className="mt-4 grid gap-3 font-body text-sm sm:grid-cols-[180px_1fr]">
                  <dt className="text-[#8A93A8]">Intestatario Conto</dt>
                  <dd className="text-[#16256B]">Masseria Alti Pareti Soc. Agr Arl &mdash; Secolario &mdash; Masseria del Viverbene</dd>
                  <dt className="text-[#8A93A8]">Banca</dt>
                  <dd className="text-[#16256B]">Banca Intesa San Paolo</dd>
                  <dt className="text-[#8A93A8]">IBAN</dt>
                  <dd className="font-display font-bold tracking-wider text-[#16256B]" data-testid="iban-secolario">IT77S0306979724100000003520</dd>
                  <dt className="text-[#8A93A8]">Causale</dt>
                  <dd className="text-[#16256B]">Cena del ___ / Pernotto del ___ n. persone ___ Semi di Impresa 2026 &mdash; Nome e Cognome</dd>
                </dl>
                <p className="mt-5 border-t border-[#E4E8F2] pt-4 font-body text-xs italic text-[#4A5268]">
                  La prenotazione cena/pernotto si intende perfezionata e confermata solo al ricevimento della contabile dell&rsquo;avvenuto pagamento (inviare la stessa a <a href="mailto:sales@secolario.com" className="font-semibold text-[#2D46A0] no-underline">sales@secolario.com</a>).
                </p>
              </div>
            </section>

            {/* 05 Condizioni */}
            <section className="rounded-3xl border border-[#E4E8F2] bg-white p-6 shadow-sm sm:p-10">
              <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">05 · Condizioni</p>
              <h2 className="mt-3 font-display text-2xl font-black uppercase text-[#16256B] sm:text-3xl">
                Termini, Condizioni e Recesso
              </h2>
              <ul className="mt-6 space-y-4 font-body text-sm leading-relaxed text-[#4A5268]">
                <li>
                  <strong className="text-[#16256B]">Conferma dell&rsquo;iscrizione.</strong> L&rsquo;iscrizione è subordinata alla disponibilità dei posti e al versamento dell&rsquo;acconto.
                </li>
                <li>
                  <strong className="text-[#16256B]">Cancellazione da parte del partecipante.</strong> In caso di disdetta comunicata via e-mail a <a href="mailto:segreteria@fondazionesemidimpresa.it" className="text-[#2D46A0] underline">segreteria@fondazionesemidimpresa.it</a> entro il 09/11/2026, l&rsquo;acconto verrà restituito. Oltre tale termine, l&rsquo;acconto non sarà rimborsabile.
                </li>
                <li>
                  <strong className="text-[#16256B]">Annullamento del corso.</strong> Qualora il corso venga annullato dall&rsquo;organizzatore per cause di forza maggiore o mancato raggiungimento del numero minimo di partecipanti, le somme già versate verranno interamente rimborsate.
                </li>
              </ul>
            </section>

            {/* 06 Privacy */}
            <section className="rounded-3xl border border-[#E4E8F2] bg-white p-6 shadow-sm sm:p-10">
              <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">06 · Privacy GDPR</p>
              <h2 className="mt-3 font-display text-2xl font-black uppercase text-[#16256B] sm:text-3xl">
                Trattamento dei Dati Personali
              </h2>
              <p className="mt-4 font-body text-sm leading-relaxed text-[#4A5268]">
                Ai sensi del Regolamento UE 2016/679 (GDPR), i dati forniti saranno trattati esclusivamente per la gestione amministrativa, contabile ed organizzativa della sessione di formazione.
              </p>

              <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-[#E4E8F2] p-4 transition-colors hover:bg-[#F4F6FB]" data-testid="consent-dati-label">
                <input
                  type="checkbox"
                  checked={form.consenso_dati}
                  onChange={update("consenso_dati")}
                  className="mt-1 h-4 w-4 shrink-0 accent-[#16256B]"
                  data-testid="checkbox-consenso-dati"
                  required
                />
                <span className="font-body text-sm leading-relaxed text-[#4A5268]">
                  <strong className="text-[#16256B]">Presto il consenso</strong> al trattamento dei dati per finalità organizzative e amministrative. <span className="text-[#C9A227]">(obbligatorio)</span>
                </span>
              </label>

              <p className="mt-6 font-body text-sm text-[#4A5268]">
                Consenso all&rsquo;invio di comunicazioni promozionali su futuri corsi <em>(opzionale)</em>:
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${form.consenso_marketing === true ? "border-[#16256B] bg-[#16256B]/5" : "border-[#E4E8F2] hover:bg-[#F4F6FB]"}`} data-testid="marketing-yes-label">
                  <input
                    type="radio"
                    name="consenso_marketing"
                    checked={form.consenso_marketing === true}
                    onChange={() => setForm((f) => ({ ...f, consenso_marketing: true }))}
                    className="h-4 w-4 accent-[#16256B]"
                    data-testid="radio-marketing-yes"
                  />
                  <span className="font-body text-sm text-[#16256B]">Presto il consenso</span>
                </label>
                <label className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${form.consenso_marketing === false ? "border-[#16256B] bg-[#16256B]/5" : "border-[#E4E8F2] hover:bg-[#F4F6FB]"}`} data-testid="marketing-no-label">
                  <input
                    type="radio"
                    name="consenso_marketing"
                    checked={form.consenso_marketing === false}
                    onChange={() => setForm((f) => ({ ...f, consenso_marketing: false }))}
                    className="h-4 w-4 accent-[#16256B]"
                    data-testid="radio-marketing-no"
                  />
                  <span className="font-body text-sm text-[#16256B]">Nego il consenso</span>
                </label>
              </div>
            </section>

            {/* Submit */}
            <div className="flex flex-col gap-5 rounded-3xl border border-[#E4E8F2] bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-10">
              <div>
                <p className="font-display text-lg font-extrabold text-[#16256B]">
                  Pronto a inviare la richiesta?
                </p>
                <p className="mt-1 font-body text-sm text-[#4A5268]">
                  La Segreteria la contatterà via telefono o email entro 24 ore per i dettagli operativi.
                </p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-3 rounded-full bg-[#C9A227] px-8 py-4 font-display text-base font-bold text-[#16256B] transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="submit-iscrizione-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Invio in corso...
                  </>
                ) : (
                  "Invia il modulo di prenotazione"
                )}
              </button>
            </div>
          </form>
        )}
      </main>

      <footer className="border-t border-[#E4E8F2] bg-white py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-5 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="font-body text-xs text-[#8A93A8]">
            © {new Date().getFullYear()} Fondazione Semi d&rsquo;Impresa &ldquo;Luca Delle Donne&rdquo; Impresa Sociale ETS
          </p>
          <p className="font-body text-xs text-[#8A93A8]">
            Segreteria: 0832 / 393823 &middot; segreteria@fondazionesemidimpresa.it
          </p>
        </div>
      </footer>
    </div>
  );
}
